import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { DatePipe } from '@angular/common';

import { FormArray, FormGroup, FormControl, FormBuilder, AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatDialog, MatDialogModule, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { Subject, BehaviorSubject, Observable, pipe, Subscription, switchMap, catchError, of, tap, map, take, throwError } from 'rxjs';

import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MercalinkFselibService, MercalinkAPIService } from '../mercalink-fselib.service';
import * as dao from '../shared/dao';
import * as utils from '../shared/utils';

export interface AttachDialogData {
  server_url: string;
  area_baseurl: string;
  fse_id: number;
  numIdentFact: string;
  factureFR: dao.FactureFR;
  etat_facture?: string;
}

@Component({
  selector: 'lib-attach',
  standalone: true,
  imports: [MatDialogModule, MatDialogContent, MatButtonModule, MatDialogActions, MatIconModule, MatFormFieldModule, MatInputModule, FormsModule, ReactiveFormsModule, MatOptionModule, MatTableModule, DatePipe, MatTooltipModule],
  templateUrl: './attach.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './attach.component.scss',
})
export class AttachComponent {
  readonly dialogRef = inject(MatDialogRef<AttachComponent>);
  readonly dialogdata: AttachDialogData = inject<AttachDialogData>(MAT_DIALOG_DATA as any); // 'as any' tricks the compiler

  public errormsg: string | undefined = undefined;
  public docs?: any[];
  public newDoc_nature: string = '-';
  public newDoc_typepj: string = 'PDF';
  public newDoc_idpj: string = '';
  public objectKeys = Object.keys; // to use *ngFor with object keys
  public docNatureOptions = dao.DOC_NATURE_OPTIONS;
  public ETATDOSSIER = dao.ETATDOSSIER;
  public CODEACTION = dao.CODEACTION;

  constructor(
    public apiService: MercalinkAPIService,
    public fseService: MercalinkFselibService,
  ) {}

  ngOnInit() {
    this.loadAttachs$().subscribe({
      next: (_docs: dao.FseDoc[]) => {},
      error: error => {
        console.error('Request failed', error);
        this.errormsg = error instanceof Error ? error.message : JSON.stringify(error);
      },
    });
  }

  loadAttachs$(): Observable<dao.FseDoc[]> {
    console.log('[loadAtachs] About to call getDocsByFseId$');
    return this.apiService.getDocsByFseId$(this.dialogdata.server_url, this.dialogdata.fse_id).pipe(
      tap((docs: any[]) => {
        console.log('[loadAtachs] nb attachs: ', docs.length);
        this.docs = docs;
      }),
    );
  }

  showPdf(doc_id: number) {
    this.apiService
      .getDocById$(this.dialogdata.server_url, doc_id)
      .pipe(
        // Get doc by fse_doc.id w/ content
        tap((docs: dao.FseDoc[]) => {
          // TODO add FseDOc type
          const doc = docs[0];
          console.log('[showPdf] doc: ', JSON.stringify(doc, null, 2));

          if (!doc.content) {
            this.errormsg = 'Le contenu du document est vide ou indisponible.';
            return;
          }

          const byteArray = doc.content; // This is the array of numbers

          // Convert to Uint8Array
          const pdfData = new Uint8Array(byteArray);

          // Create a Blob with PDF MIME type
          const blob = new Blob([pdfData], { type: 'application/pdf' });

          // Create a URL for the Blob
          const url = window.URL.createObjectURL(blob);

          // Option 1: Open in a new tab
          window.open(url);
        }),
      )
      .subscribe({
        next: (docs: any[]) => {},
        error: error => {
          console.error('Request failed', error);
          this.errormsg = error instanceof Error ? error.message : JSON.stringify(error);
        },
      });
  }

  supprimerPJ(d: dao.FseDoc) {
    if (!d.idpj || this.dialogdata.etat_facture === 'MODELE_LOCATION') {
      // Document only exists in local database (not registered in AreaFSE).
      // Directly delete the local fse_doc record without calling AreaFSE.
      this.apiService
        .postDocDeleteEvent$(this.dialogdata.server_url, d)
        .pipe(switchMap(() => this.loadAttachs$()))
        .subscribe({
          error: error => console.error('[supprimerPJ] Error deleting local fse_doc:', error),
        });
      return;
    }

    let dateFacture = utils.tofseformat(this.dialogdata.factureFR.dateFacture);
    const supprimerPJ$ = this.fseService.supprimerPJ$(this.dialogdata.area_baseurl, this.dialogdata.numIdentFact, this.dialogdata.factureFR.numFacture, dateFacture, d.idpj).pipe(
      tap((supprimerPJHTTPResponse: dao.SupprimerHTTPResponse) => {
        console.log('Back from supprimerPJ$. supprimerPJHTTPResponse: ' + JSON.stringify(supprimerPJHTTPResponse.HTTPResponse, null, 2));
      }),
      switchMap((supprimerPJHTTPResponse: dao.SupprimerHTTPResponse) => {
        const codeRetour = Number(supprimerPJHTTPResponse.HTTPResponse.codeRetour);
        if (codeRetour < 0) {
          alert(`Erreur à la suppression: ${supprimerPJHTTPResponse.HTTPResponse.libRetour}`);
          return of(null); // Complete gracefully and do not proceed with postDocDeleteEvent$
        } else {
          // Assuming codeRetour === 0 for success
          // Only execute postDocDeleteEvent$ if the Area API call was successful (codeRetour === 0)
          return this.apiService.postDocDeleteEvent$(this.dialogdata.server_url, d).pipe(
            switchMap(() => this.loadAttachs$()), // Reload attachments after successful deletion
          );
        }
      }),
    );

    supprimerPJ$.subscribe({
      next: (_: any) => {
        // Optionally, handle success here
      },
      error: error => {
        console.error('Request failed', error);
        this.errormsg = error instanceof Error ? error.message : JSON.stringify(error);
        alert("Erreur fatale, la PJ n'a pas été supprimée");
      },
    });
  }

  // File is uploaded => fse_doc.id, now call Area 'ajouterPJ' to get an idpj and update fse_doc.idpj
  ajouterpj$(id: number): Observable<any> {
    // Add attachement to Area DB
    return this.apiService.getDocById$(this.dialogdata.server_url, id).pipe(
      // Get FSE_DOC (w/ content) from doc id. Always returns a vec, potentially empty
      tap((docs: dao.FseDoc[]) => {
        console.log('[ajouterpj$ doc: ', docs);
      }),
      switchMap((docs: dao.FseDoc[]) => {
        // Should by only one doc
        const doc = docs[0]; // Raw (not sent to Area) fse_doc from doc upload
        const pj: dao.PJ = {
          nature: docs[0].nature,
          idFactResponsable: this.dialogdata.numIdentFact,
          dateConstitution: utils.tofseformat(this.dialogdata.factureFR.prescription.datePresc) + '000000',
          type: docs[0].typepj,
          idPJ: undefined, // Adding it is mandatory to please the compiler => need to delete it afterward
          contenu: docs[0].content_base64,
        };
        if (pj.type !== 'PDF') {
          delete pj['contenu'];
        }
        if (pj.type === 'PDF' || pj.type === 'PAPIER') {
          delete pj['idPJ'];
        } else {
          // REF_EXTERNE we need idPJ
          pj.idPJ = this.newDoc_idpj;
        }
        let dateFacture = utils.tofseformat(this.dialogdata.factureFR.dateFacture);
        return this.fseService.ajouterPJ$(this.dialogdata.area_baseurl, this.dialogdata.numIdentFact, this.dialogdata.factureFR.numFacture, dateFacture, pj).pipe(
          catchError(httpError => {
            console.error('[ajouterpj$] HTTP error calling AreaFSE ajouterPJ:', httpError);
            const errMsg = httpError?.error?.message || httpError?.message || 'Erreur HTTP de communication avec AreaFSE';
            alert(`Erreur fatale à l'ajout de la P.J.: ${errMsg}`);
            this.supprimerPJ(doc); // Remove raw fse_doc on HTTP error
            return of(null);
          }),
          tap((ajouterPJHTTPResponse: dao.AjouterPJHTTPResponse | null) => {
            if (ajouterPJHTTPResponse) {
              console.log('Back from ajouterPJ$. AjouterPJHTTPResponse: ' + JSON.stringify(ajouterPJHTTPResponse.HTTPResponse.PJ, null, 2));
            }
          }),
          switchMap((ajouterPJHTTPResponse: dao.AjouterPJHTTPResponse | null) => {
            if (!ajouterPJHTTPResponse) {
              return of(null);
            }
            const codeRetour = Number(ajouterPJHTTPResponse.HTTPResponse.codeRetour);
            if (codeRetour < 0) {
              alert(`Erreur à l'ajout de la P.J. : ${ajouterPJHTTPResponse.HTTPResponse.libRetour}`);
              this.supprimerPJ(doc); // Remove raw fse_doc
              return of(null); // Complete gracefully and not continue
            }

            doc.idpj = ajouterPJHTTPResponse.HTTPResponse.PJ.idPJ;
            return this.apiService.postDocUpdateIdPJEvent$(this.dialogdata.server_url, doc).pipe(
              catchError(error => {
                console.error('Error in postDocUpdateIdPJEvent:', error);
                return throwError(() => error);
              }),
              switchMap(() => {
                return this.loadAttachs$();
              }),
            );
          }),
        );
      }),
    );
  }

  // New method to handle the "Ajouter" button click
  onAddAttachment() {
    console.log('[onAddAttachment] newDoc_typepj: ', this.newDoc_typepj);
    if (this.newDoc_typepj === 'PDF') {
      // If PDF, trigger the hidden file input click to open the file selection dialog
      // The actual file upload will then be handled by onFileSelected
      const fileInput = document.getElementById('myFile') as HTMLInputElement;
      if (fileInput) {
        fileInput.click(); // Triggers onFileSelected()
      }
    } else {
      // For non-PDF types, create a dummy file and proceed with upload
      let fileName: string;
      let fileContent: string;

      switch (this.newDoc_typepj) {
        case 'PAPIER':
          fileName = `Document PAPIER`;
          fileContent = '';
          break;
        case 'REF_EXTERNE':
          // For REF_EXTERNE, use only the last segment of newDoc_idpj for the filename
          fileName = `Réf. Ext. ${this.newDoc_idpj.split('.').pop() || this.newDoc_idpj}`;
          fileContent = '';
          break;
        default: // Should NOT happen
          fileName = `Document - ${new Date().toISOString()}.txt`;
          fileContent = `Type: ${this.newDoc_typepj}, Nature: ${this.docNatureOptions[this.newDoc_nature] || this.newDoc_nature}`;
          break;
      }

      // Create an empty or minimal Blob as the "file" part
      const emptyFileBlob = new Blob([fileContent], { type: 'text/plain' });
      const dummyFile = new File([emptyFileBlob], fileName, { type: 'text/plain' });

      // Call the internal upload logic
      this._initiateUpload(dummyFile, fileName, this.newDoc_typepj, this.newDoc_nature);
    }
  }

  onFileSelected(event: any) {
    // File is selected, save fse_doc w/ postAttach$, then attach to Area
    const input = event.target as HTMLInputElement;
    const file: File = event.target.files[0];
    console.log('[onFileSelected] File selected. newDoc_typepj: ', this.newDoc_typepj);
    console.log(file);
    if (file) {
      // For file selection, assume type is PDF and use the component's current newDoc_nature
      this._initiateUpload(file, file.name, this.newDoc_typepj, this.newDoc_nature);
      input.value = ''; // Reset the input value so selecting the same file again works
    }
  }

  private _initiateUpload(file: File, fileName: string, typepj: string, nature: string) {
    const formData: FormData = new FormData();
    formData.append('file', file);
    const jsonPayload: any = {
      name: fileName,
      id_fse: this.dialogdata.fse_id,
      numIdentFact: this.dialogdata.numIdentFact,
      typepj: typepj,
      nature: nature,
    };
    if (typepj === 'REF_EXTERNE') {
      jsonPayload.idPJ = this.newDoc_idpj;
    }
    const jsonBlob = new Blob([JSON.stringify(jsonPayload)], { type: 'application/json' });
    formData.append('json', jsonBlob);

    const upload$ = this.apiService.postAttach$(this.dialogdata.server_url, formData).pipe(
      tap((res: any) => {
        console.log('[AttachComponent] postAttach$ res: ', res);
      }),
      switchMap((res: any) => {
        if (this.dialogdata.etat_facture === 'MODELE_LOCATION') {
          return this.loadAttachs$();
        }
        return this.ajouterpj$(res.data.id);
      }),
    );

    upload$.subscribe({
      next: (_: any) => {}, // Success handled by ajouterpj$ and loadAttachs$
      error: error => {
        console.error('Request failed', error);
        this.errormsg = error instanceof Error ? error.message : JSON.stringify(error);
        alert("Erreur fatale, la PJ n'a pas été créée");
      },
    });
  }

  onClose(save: boolean) {
    if (save) {
      this.dialogRef.close({});
    } else {
      this.dialogRef.close(undefined);
    }
  }
}
